import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useIwEditingStore } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import { useIwRecordingStore } from '@/entities/interwills/stores/iwRecording.store';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useProfileStore } from '@/shared/store/profile.store';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { VideoSavedModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

export const VideoSavedModal: FC<VideoSavedModalProps> = ({ closeRecording }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'videoSaved' });
  const modals = useModalsStore();
  const recording = useIwRecordingStore();
  const { testator, witnesses } = useIwEditingStore();
  const { isBeneficiary } = useProfileStore();

  const onFinish = () => {
    if (isBeneficiary) {
      modals.closeModal();
      closeRecording();
      return;
    }

    if (!recording.testator) {
      const name = getBeneficiaryFullName({
        firstName: testator.firstName ?? '',
        lastName: testator.lastName ?? '',
      });

      modals.closeModal();
      modals.openModal({
        type: 'VideoRecordNextModal',
        role: t('testator'),
        name,
        onBack: () => {
          modals.closeModal();
        },
        onNext: () => {
          recording.setRecordingFor({
            name,
            role: t('testator'),
            witnessId: null,
          });
        },
      });

      return;
    }

    const next = Object.entries(recording.witnesses).find((item) => item[1] === null);
    const nextId = next?.[0];

    if (nextId) {
      const index = witnesses.findIndex((item) => item.id === nextId);
      const witness = witnesses[index];
      const name = getBeneficiaryFullName({
        firstName: witness.firstName ?? '',
        lastName: witness.lastName ?? '',
      });
      modals.closeModal();
      modals.openModal({
        type: 'VideoRecordNextModal',
        role: t('witness'),
        name,
        onBack: () => {
          modals.closeModal();
        },
        onNext: () => {
          recording.setRecordingFor({
            name,
            role: t('witness'),
            witnessId: nextId,
          });
        },
      });

      return;
    }

    modals.closeModal();
    closeRecording();
  };

  return (
    <ModalContainerV2Styled pinBottom title="" closable={false}>
      <Container>
        <Cross>
          <Icon name="check" size="s24" />
        </Cross>
        <TitleStyled>{t('title')}</TitleStyled>
        <Content>
          <span>{t('text')}</span>
        </Content>
        <Buttons>
          <ButtonIw onClick={onFinish}>{t('btn')}</ButtonIw>
        </Buttons>
      </Container>
    </ModalContainerV2Styled>
  );
};

const Cross = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.systemForest200};
  background: ${COLORS.systemForest100};
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  font-size: 14px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 32px;

  ${media.iwMobile} {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
`;

const TitleStyled = styled(Title)`
  font-size: 22px !important;
`;

const ModalContainerV2Styled = styled(ModalContainerV2)`
  width: 400px !important;

  ${media.mobile} {
    width: 100% !important;
  }
`;
